const BLOB_W = 240;
const BLOB_H = 240;
const OFFSCREEN = { x: -(BLOB_W + 60), y: -(BLOB_H + 60), opacity: 0 };
const HIDDEN = { opacity: 0, scale: 1.15, rotation: -20 };
const LABEL_HIDDEN = { opacity: 0, y: 6 };

interface UseSpotlightAnimationOptions {
  selector?: string;
  trigger?: Ref<boolean>;
}

/**
 * Returns a point offset perpendicularly from the midpoint of A→B.
 * `side` alternates ±1 so successive hops weave left/right naturally.
 * Arc magnitude is proportional to distance (capped at 110px).
 */
const arcPoint = (ax: number, ay: number, bx: number, by: number, side: number) => {
  const mx = (ax + bx) / 2;
  const my = (ay + by) / 2;
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  const mag = Math.min(len * 0.25, 75);
  return { x: mx + (-dy / len) * mag * side, y: my + (dx / len) * mag * side };
};

export const useSpotlightAnimation = (
  blobRef: Ref<HTMLElement | null>,
  options: UseSpotlightAnimationOptions = {},
) => {
  const { $gsap } = useNuxtApp();
  const { selector = '[data-blob-img]', trigger } = options;

  let tl: gsap.core.Timeline | null = null;
  let stopWatch: (() => void) | null = null;

  const start = (blob: HTMLElement) => {
    const blobW = blob.offsetWidth || BLOB_W;
    const blobH = blob.offsetHeight || BLOB_H;
    const targets = Array.from(document.querySelectorAll<HTMLElement>(selector));
    if (!targets.length) {
      return;
    }

    targets.forEach((target) => {
      const imgEl = target.querySelector<HTMLElement>('[data-blob-image]');
      const labelEl = target.querySelector<HTMLElement>('[data-blob-label]');
      $gsap.set(imgEl ?? target, HIDDEN);
      if (labelEl) {
        $gsap.set(labelEl, LABEL_HIDDEN);
      }
    });

    const loop = $gsap.timeline({ repeat: -1, repeatDelay: 1 });

    // Track previous position so arc points reflect the actual travel path
    let px = -(blobW + 60);
    let py = -(blobH + 60);

    targets.forEach((target, i) => {
      const { left, top, width, height } = target.getBoundingClientRect();
      const x = left + width / 2 - blobW / 2;
      const y = top + height / 2 - blobH / 2;
      const arc = arcPoint(px, py, x, y, i % 2 === 0 ? 1 : -1);

      // Curved hop: gentle sine easing for a floaty, unhurried feel
      loop.to(blob, {
        keyframes: [
          { x: arc.x, y: arc.y, ease: 'sine.in' },
          { x, y, ease: 'sine.out' },
        ],
        duration: 1.4,
      });
      // Fade blob in on first entry; subsequent hops keep opacity at 0.8
      if (i === 0) {
        loop.to(blob, { opacity: 0.8, duration: 0.8, ease: 'sine.out' }, '<');
      }

      // Opacity fades in smoothly; scale+rotation settles gently — concurrent
      const imgEl = target.querySelector<HTMLElement>('[data-blob-image]');
      const labelEl = target.querySelector<HTMLElement>('[data-blob-label]');
      const animTarget = imgEl ?? target;
      loop.to(animTarget, { opacity: 1, duration: 0.7, ease: 'sine.out' }, '-=0.4');
      loop.to(animTarget, { rotation: 0, duration: 0.9, ease: 'wordPop' }, '<');
      if (labelEl) {
        loop.to(labelEl, { opacity: 1, y: 0, duration: 0.5, ease: 'sine.out' }, '+=0.15');
      }

      // Dwell longer so the viewer has time to read, then soft dismiss
      const dwellTime = labelEl ? '+=1.0' : '+=1.2';
      loop.to(animTarget, { ...HIDDEN, rotation: 20, duration: 0.5, ease: 'exit' }, dwellTime);
      if (labelEl) {
        loop.to(labelEl, { ...LABEL_HIDDEN, duration: 0.4, ease: 'exit' }, '<');
      }

      px = x;
      py = y;
    });

    // Curved exit off-screen right — slow, calm fade out
    const exitArc = arcPoint(px, py, window.innerWidth + blobW, py, -1);
    loop.to(blob, {
      keyframes: [
        { x: exitArc.x, y: exitArc.y, ease: 'sine.in' },
        { x: window.innerWidth + blobW, y: py, ease: 'sine.out' },
      ],
      duration: 1.1,
    });
    loop.to(blob, { opacity: 0, duration: 0.6, ease: 'sine.in' }, '<');
    loop.set(blob, OFFSCREEN);

    tl = loop;
  };

  onMounted(() => {
    const blob = blobRef.value;
    if (!blob) {
      return;
    }

    const blobW = blob.offsetWidth || BLOB_W;
    const blobH = blob.offsetHeight || BLOB_H;
    $gsap.set(blob, { x: -(blobW + 60), y: -(blobH + 60), opacity: 0 });

    if (trigger) {
      // Fires immediately if loader was already skipped, or waits for it
      stopWatch = watch(trigger, (gone) => {
        if (!gone) {
          return;
        }
        stopWatch?.();
        stopWatch = null;
        nextTick(() => start(blob));
      }, { immediate: true });
    }
    else {
      nextTick(() => start(blob));
    }
  });

  onBeforeUnmount(() => {
    stopWatch?.();
    tl?.kill();
    stopWatch = null;
    tl = null;
  });
};
